import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLang } from './LangContext';
import '../styles/About.css';

/**
 * AboutSlideshowFrame
 * A single auto-rotating image frame used by the About-page gallery. Each
 * frame runs its own timer and crossfades between images. Admins populate
 * the content from the Dashboard; if a slot is empty we render a subtle
 * placeholder so the layout stays intact and doesn't jump once the first
 * image is uploaded.
 */
const AboutSlideshowFrame = ({ images, intervalMs = 5000, className = '', ariaLabel }) => {
  const lang = useLang();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // Reset cursor whenever the image list changes so we never try to show
    // an index that no longer exists after a deletion.
    if (idx >= images.length) setIdx(0);
  }, [images.length, idx]);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(t);
  }, [images, intervalMs]);

  if (!images || images.length === 0) {
    return (
      <div className={`about-slide-frame about-slide-frame-empty ${className}`} aria-label={ariaLabel}>
        <div className="about-slide-placeholder">
          <span>📷</span>
          <small>{lang.ABOUT_NO_IMAGE || 'Chưa có ảnh'}</small>
        </div>
      </div>
    );
  }

  return (
    <div className={`about-slide-frame ${className}`} aria-label={ariaLabel}>
      {images.map((im, i) => (
        <img
          key={im.id || im.filename}
          src={`/uploads/about-gallery/${im.filename}`}
          alt={im.caption || ''}
          className={`about-slide-image ${i === idx ? 'is-active' : ''}`}
          loading="lazy"
        />
      ))}
      {images[idx] && images[idx].caption && (
        <div className="about-slide-caption">{images[idx].caption}</div>
      )}
      {images.length > 1 && (
        <div className="about-slide-dots" aria-hidden="true">
          {images.map((_, i) => (
            <span key={i} className={`about-slide-dot ${i === idx ? 'is-active' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const About = ({ lang }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [gallery, setGallery] = useState({ main: [], sideTop: [], sideBottom: [] });
  const [content, setContent] = useState({});

  useEffect(() => {
    // Public endpoint — no auth required. Failures are non-fatal; the page
    // just renders without the gallery section.
    fetch('/api/about-gallery')
      .then(r => r.ok ? r.json() : { main: [], sideTop: [], sideBottom: [] })
      .then(d => setGallery({
        main: Array.isArray(d.main) ? d.main : [],
        sideTop: Array.isArray(d.sideTop) ? d.sideTop : [],
        sideBottom: Array.isArray(d.sideBottom) ? d.sideBottom : []
      }))
      .catch(() => {});
    fetch('/api/about-content')
      .then(r => r.ok ? r.json() : {})
      .then(d => setContent(d && typeof d === 'object' ? d : {}))
      .catch(() => {});
  }, []);

  const pick = (key, fallback) => (content[key] && String(content[key]).trim()) || fallback;

  const stats = [
    { value: pick('statMembers', lang.STAT_MEMBERS || '150+'), label: pick('statMembersLabel', lang.STAT_MEMBERS_LABEL || 'Members') },
    { value: pick('statCtfs', lang.STAT_CTFS || '30+'), label: pick('statCtfsLabel', lang.STAT_CTFS_LABEL || 'CTFs Participated') },
    { value: pick('statWorkshops', lang.STAT_WORKSHOPS || '20+'), label: pick('statWorkshopsLabel', lang.STAT_WORKSHOPS_LABEL || 'Workshops Organized') },
    { value: pick('statYears', lang.STAT_YEARS || '7+'), label: pick('statYearsLabel', lang.STAT_YEARS_LABEL || 'Years Active') },
  ];

  const values = [
    {
      icon: '01',
      title: pick('missionTitle', lang.ABOUT_MISSION_TITLE || 'Mission'),
      desc: pick('missionDesc', lang.ABOUT_MISSION_DESC || 'Build a community of students passionate about information security.'),
    },
    {
      icon: '02',
      title: pick('visionTitle', lang.ABOUT_VISION_TITLE || 'Vision'),
      desc: pick('visionDesc', lang.ABOUT_VISION_DESC || 'Become the leading information security club at PTIT.'),
    },
    {
      icon: '03',
      title: pick('valuesTitle', lang.ABOUT_VALUES_TITLE || 'Core Values'),
      desc: pick('valuesDesc', lang.ABOUT_VALUES_DESC || 'Learn - Share - Create - Responsibility.'),
    },
  ];

  const hasAnyGalleryImage = (gallery.main.length + gallery.sideTop.length + gallery.sideBottom.length) > 0;

  return (
    <section className="about section" id="about" ref={ref}>
      <div className="container">
        <div className="about-grid">
          <motion.div
            className="about-left"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="terminal-section-header">
                <div className="terminal-section-titlebar">
                  root@ispclub:~/# cd /ispclub/about
                </div>
                <div className="terminal-section-body">
                  <span className="terminal-sh-line">&gt;_ sh about.sh</span>
                </div>
              </div>
            <h2 className="section-title">{pick('title', lang.ABOUT_TITLE || 'What is ISP Club?')}</h2>
            <p className="about-description">
              {pick('description', lang.ABOUT_DESCRIPTION || 'ISP Club is an academic club focused on Information Security at PTIT.')}
            </p>

            {/* Stats */}
            <div className="about-stats">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="stat-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                >
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </motion.div>
              ))}
            </div>

            {/* Two small slideshow frames sit DIRECTLY under the stats grid
                so there's no extra margin-row between them. They share a
                row below stats and their top edge lines up with the top
                of the big square in the right column. */}
            {hasAnyGalleryImage && (
              <motion.div
                className="about-gallery-side"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.6 }}
              >
                <AboutSlideshowFrame
                  images={gallery.sideTop}
                  intervalMs={4200}
                  className="about-slide-side"
                  ariaLabel="Ảnh phụ 1"
                />
                <AboutSlideshowFrame
                  images={gallery.sideBottom}
                  intervalMs={6000}
                  className="about-slide-side"
                  ariaLabel="Ảnh phụ 2"
                />
              </motion.div>
            )}
          </motion.div>

          <motion.div
            className="about-right"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {values.map((item, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.15, duration: 0.5 }}
                whileHover={{ x: 8 }}
              >
                <div className="value-number">{item.icon}</div>
                <div className="value-content">
                  <h3 className="value-title">{item.title}</h3>
                  <p className="value-desc">{item.desc}</p>
                </div>
              </motion.div>
            ))}

            {/* Big square slideshow sits DIRECTLY under the last Core
                Values card in the same right column, so there's no large
                gap between the card and the image. */}
            {hasAnyGalleryImage && (
              <motion.div
                className="about-slide-main-wrap"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <AboutSlideshowFrame
                  images={gallery.main}
                  intervalMs={5000}
                  className="about-slide-main"
                  ariaLabel="Ảnh nổi bật"
                />
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
